import { useOrganization } from '@clerk/clerk-react'
import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import type { SeatInfo } from '@/components/billing/billing-seat.interface'
import { useSeats } from '@/hooks/billing/use-seats.hook'
import { orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

export interface UseSubscriptionSeatsReturn {
  seatInfo: SeatInfo | null
  isLoadingSeats: boolean
  isAddingSeats: boolean
  isRemovingSeats: boolean
  currentMemberCount: number
  isWarningOpen: boolean
  warningMemberCount: number
  setIsWarningOpen: (isOpen: boolean) => void
  handleAddSeats: (quantity: number) => Promise<void>
  handleRemoveSeats: (quantity: number) => Promise<void>
  handleGoToMembers: () => void
}

export const useSubscriptionSeats = (): UseSubscriptionSeatsReturn => {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const navigate = useNavigate()
  const { memberships } = useOrganization({ memberships: { pageSize: 50 } })

  const currentMemberCount = memberships?.data?.length ?? 0
  const {
    seatInfo,
    isLoading: isLoadingSeats,
    isAddingSeats,
    isRemovingSeats,
    addSeats,
    removeSeats,
  } = useSeats(currentMemberCount)

  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [warningMemberCount, setWarningMemberCount] = useState(0)

  const handleAddSeats = useCallback(
    async (quantity: number): Promise<void> => {
      await addSeats(quantity)
    },
    [addSeats]
  )

  const handleRemoveSeats = useCallback(
    async (quantity: number): Promise<void> => {
      const result = await removeSeats(quantity, currentMemberCount)
      if (!result.success && result.reason === 'member_count_exceeded') {
        setWarningMemberCount(result.currentMemberCount)
        setIsWarningOpen(true)
      }
    },
    [removeSeats, currentMemberCount]
  )

  const handleGoToMembers = useCallback((): void => {
    navigate(`/${orgSlug}/${orgRoutes.settings}/${settingsRoutes.members}`)
  }, [navigate, orgSlug])

  return {
    seatInfo,
    isLoadingSeats,
    isAddingSeats,
    isRemovingSeats,
    currentMemberCount,
    isWarningOpen,
    warningMemberCount,
    setIsWarningOpen,
    handleAddSeats,
    handleRemoveSeats,
    handleGoToMembers,
  }
}
